/** UNDE SE ȚINE hârtia de calc. Ce ÎNSEAMNĂ ea e în `calc.ts`.
 *
 *  A stat pe aparat, în `localStorage`, și n-a ținut: o poză de plan e un
 *  megaoctet, cota unei origini e de câțiva, iar scrierea care nu încăpea
 *  arunca hârtiile celorlalte module și încerca din nou. La al treilea modul
 *  trasat, primul rămânea fără poză — și nimeni nu afla, fiindcă răspunsul
 *  scrierii nu-l citea nimeni. Acum are un rând pe server (migrarea 0044).
 *
 *  DOUĂ FELURI DE SCRIERE, și de-aia există fișierul ăsta în loc de două
 *  apeluri directe:
 *
 *    * POZA se schimbă rar și dintr-un gest limpede — se alege un fișier, se
 *      termină un import. Se scrie PE LOC și se așteaptă: cine a pus o poză
 *      trebuie s-o găsească acolo și după ce închide fereastra.
 *    * CALIBRAREA se schimbă des și în rafală — se trage de hârtie, se
 *      rotește, se mișcă felia de transparență. Se scrie DUPĂ O PAUZĂ și FĂRĂ
 *      poză; altfel editorul ar fi trimis un film.
 */
import {
  iaCalculModulului, iaCalculMeu, salveazaCalculModulului, salveazaCalculMeu,
  scoateCalculModulului, scoateCalculMeu,
} from "../../api";
import type { Calc } from "./calc";

/** DOUĂ COLECȚII, DOUĂ RUTE. Un `projectId` gol înseamnă colecția omului
 *  (migrarea 0045), unde hârtia stă pe (om, cod); cu proiect, pe (proiect,
 *  cod). Restul fișierului nu știe care e care. */
const ia = (pid: string, cod: string) =>
  (pid ? iaCalculModulului(pid, cod) : iaCalculMeu(cod));
const scrie = (pid: string, cod: string, c: Partial<Calc>) =>
  (pid ? salveazaCalculModulului(pid, cod, c) : salveazaCalculMeu(cod, c));
const scoate = (pid: string, cod: string) =>
  (pid ? scoateCalculModulului(pid, cod) : scoateCalculMeu(cod));

/** Cât se așteaptă după ultima mișcare înainte de a scrie calibrarea. Destul
 *  cât o tragere întreagă să fie o singură scriere, destul de puțin cât să nu
 *  se piardă la o închidere de filă. */
const PAUZA_MS = 400;

const cheia = (projectId: string, cod: string) =>
  `${projectId}.${cod.toUpperCase()}`;

/** Ce poză are serverul, pe cheie. Cu ea se știe dacă scrierea trebuie s-o mai
 *  care o dată sau e vorba doar de câteva numere. */
const laServer = new Map<string, string>();
/** Scrierea de calibrare programată pe fiecare cheie, cu firul de care atârnă
 *  promisiunea ei: o scriere nouă o ÎNLOCUIEȘTE pe cea care aștepta, iar
 *  promisiunea aceleia se încheie odată cu ea — cine o aștepta voia să știe că
 *  schimbarea lui a fost acoperită, iar scrierea care urmează o acoperă. */
const asteptare = new Map<string, {
  fir: ReturnType<typeof setTimeout>; incheie: () => void;
}>();
/** Scrierile pornite și neterminate — `asteaptaCalcul` le poate aștepta. */
const inZbor = new Set<Promise<unknown>>();

function urmareste<T>(p: Promise<T>): Promise<T> {
  inZbor.add(p);
  void p.catch(() => {}).finally(() => inZbor.delete(p));
  return p;
}

/** CHEIA DE PE APARAT, de pe vremea când hârtia stătea în `localStorage`.
 *
 *  Cine are una acolo o are chiar acum sub desen; dacă mutarea pe server ar fi
 *  însemnat pur și simplu că de mâine nu mai e, reparația s-ar fi văzut ca o
 *  stricăciune. Se urcă la prima deschidere și se șterge de pe aparat. Codul
 *  ăsta se poate scoate după ce trece toată lumea prin editor o dată. */
const PREFIX_VECHI = "ob.calc.";

function dePeAparat(projectId: string, cod: string): Calc | null {
  try {
    const brut = window.localStorage.getItem(
      `${PREFIX_VECHI}${projectId}.${cod.toUpperCase()}`);
    if (!brut) return null;
    const c = JSON.parse(brut) as Calc;
    return typeof c?.imagine === "string" && c.imagine ? c : null;
  } catch {
    return null;
  }
}

function uitaDePeAparat(projectId: string, cod: string): void {
  try {
    window.localStorage.removeItem(
      `${PREFIX_VECHI}${projectId}.${cod.toUpperCase()}`);
  } catch { /* fereastră privată: n-avea ce șterge */ }
}

/** Hârtia de calc a modulului, sau `null`.
 *
 *  Nu aruncă niciodată: un server care n-a răspuns înseamnă «n-are calc», nu
 *  un editor picat — desenul e oricum ce contează, hârtia e ajutorul. */
export async function iaCalcul(projectId: string,
                               cod: string): Promise<Calc | null> {
  let deSus: Calc | null = null;
  try {
    const r = await ia(projectId, cod);
    const c = r?.calc ?? null;
    if (c && typeof c.imagine === "string" && c.imagine) deSus = c;
  } catch {
    return null;   // server mut: nici nu se urcă nimic peste ce n-am putut citi
  }
  if (deSus) {
    laServer.set(cheia(projectId, cod), deSus.imagine);
    uitaDePeAparat(projectId, cod);    // a ajuns sus, n-o mai ține și aparatul
    return deSus;
  }

  // Serverul n-are: poate a rămas una de pe vremea magaziei locale.
  const vechi = dePeAparat(projectId, cod);
  if (!vechi) return null;
  // SCRIS DE-A DREPTUL, nu prin `puneCalcul`: acela înghite căderea, fiindcă
  // nimeni de pe ecran n-are ce face cu ea. Aici însă căderea e chiar ce
  // hotărăște — o hârtie ștearsă de pe aparat fără să fi ajuns sus e exact
  // pierderea tăcută de la care a pornit tot.
  try {
    await scrie(projectId, cod, vechi);
    laServer.set(cheia(projectId, cod), vechi.imagine);
    uitaDePeAparat(projectId, cod);
  } catch { /* n-a urcat: rămâne pe aparat, se mai încearcă mâine */ }
  return vechi;
}

/** O scrie. `null` o scoate.
 *
 *  Întoarce o promisiune care se încheie când scrierea A PLECAT — pentru poză
 *  asta înseamnă «serverul a primit-o», iar pentru calibrare «e programată».
 *  Cine trebuie să fie sigur (importul, care pleacă imediat spre editor) cheamă
 *  pe urmă `asteaptaCalcul`.
 */
export function puneCalcul(projectId: string, cod: string,
                           c: Calc | null): Promise<void> {
  const k = cheia(projectId, cod);
  const programat = asteptare.get(k);
  if (programat) {
    clearTimeout(programat.fir);
    asteptare.delete(k);
    programat.incheie();
  }

  if (!c) {
    laServer.delete(k);
    return urmareste(scoate(projectId, cod)
      .then(() => undefined).catch(() => undefined));
  }

  // POZĂ NOUĂ (sau prima): pe loc, cu tot cu octeți.
  if (laServer.get(k) !== c.imagine) {
    laServer.set(k, c.imagine);
    return urmareste(scrie(projectId, cod, c)
      .then(() => undefined)
      .catch(() => { laServer.delete(k); }));   // n-a intrat: se mai încearcă
  }

  // NUMAI CALIBRAREA: după pauză, fără poză.
  const { imagine: _, ...fara } = c;
  let incheie!: () => void;
  const p = urmareste(new Promise<void>((gata) => { incheie = gata; }));
  const fir = setTimeout(() => {
    asteptare.delete(k);
    void scrie(projectId, cod, fara)
      .catch(() => undefined).then(() => incheie());
  }, PAUZA_MS);
  asteptare.set(k, { fir, incheie });
  return p;
}

/** Așteaptă scrierile pornite. Pentru probe și pentru drumurile care pleacă de
 *  pe ecran imediat după ce au scris. */
export async function asteaptaCalcul(): Promise<void> {
  while (inZbor.size) await Promise.all([...inZbor]);
}

/** Uită ce știe despre server. Numai pentru probe. */
export function uitaCalcurile(): void {
  for (const a of asteptare.values()) clearTimeout(a.fir);
  asteptare.clear();
  laServer.clear();
  inZbor.clear();
}
